import { useState } from "react";
import { ChevronDown, CircleCheck, Receipt } from "lucide-react";

import type { Transaction, TransactionList } from "@/client";
import { MetricCard } from "@/components/MetricCard";
import { TransactionFiscalDocuments } from "@/components/TransactionFiscalDocuments";
import { OperatingExpenses } from "@/domain/operating-expenses";
import { FinancePresentation } from "@/presentation/finance";

type OperatingExpense = Transaction;

export function OperatingExpensesView({ data }: { data: TransactionList }) {
	const [expandedCategoryIds, setExpandedCategoryIds] = useState<string[]>([]);
	const [expandedTransactionIds, setExpandedTransactionIds] = useState<string[]>([]);
	const transactions = OperatingExpenses.list(data.transactions);
	const categories = OperatingExpenses.summarize(data.transactions);
	const totalCents = categories.reduce((total, category) => total + category.amountCents, 0);

	return (
		<>
			<section className="mb-6 grid gap-3 sm:grid-cols-3">
				<MetricCard
					label="Despesas operacionais pagas"
					value={FinancePresentation.money(totalCents)}
					detail="pela data do movimento bancário"
				/>
				<MetricCard
					label="Lançamentos classificados"
					value={String(transactions.length)}
					detail="saídas efetivadas no mês"
					tone="success"
				/>
				<MetricCard
					label="Categorias reconhecidas"
					value={String(categories.length)}
					detail="agrupamento próprio do SINDI Financeiro"
				/>
			</section>

			<section className="mb-6 rounded-3xl border border-emerald-900/10 bg-emerald-950 p-6 text-white shadow-sm">
				<div className="flex items-start gap-4">
					<span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-white/10">
						<Receipt className="size-5" />
					</span>
					<div>
						<h2 className="font-semibold">Classificações automáticas com regras explícitas</h2>
						<p className="mt-1 max-w-4xl text-sm leading-6 text-emerald-50/75">
							A categoria Tarifa reúne somente saídas efetivadas cuja descrição bancária começa com
							“TAR”; Colaboradores reconhece SISPAG Salários; Energia elétrica reconhece o recebedor
							bancário exato CEMIG Distribuição. Seguros, Manutenção, Software e serviços digitais e
							Gás de empilhadeira dependem da identidade bancária exata de fornecedores já
							confirmados. Sinais genéricos não decidem essas classificações.
						</p>
					</div>
				</div>
			</section>

			<section className="overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-sm">
				<header className="border-b border-stone-200 px-5 py-5">
					<h2 className="font-semibold text-stone-950">Pagamentos por categoria</h2>
					<p className="mt-1 text-xs text-stone-500">
						A data continua sendo a do pagamento. Quando a Qive fornece uma correspondência única, o
						documento fiscal aparece dentro do lançamento com sua própria emissão e competência.
					</p>
				</header>

				{categories.map((category) => {
					const isExpanded = expandedCategoryIds.includes(category.id);
					const transactionListId = `operating-expense-category-${category.id}`;

					return (
						<section key={category.id}>
							<button
								type="button"
								className="flex w-full items-center justify-between gap-5 border-b border-stone-100 bg-stone-50/80 px-5 py-4 text-left transition-colors hover:bg-stone-100 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-emerald-700"
								aria-controls={transactionListId}
								aria-expanded={isExpanded}
								onClick={() => {
									setExpandedCategoryIds((current) => {
										if (current.includes(category.id)) {
											return current.filter((categoryId) => categoryId !== category.id);
										}

										return [...current, category.id];
									});
								}}
							>
								<div>
									<h3 className="text-sm font-semibold text-stone-900">{category.name}</h3>
									<p className="mt-0.5 text-xs text-stone-500">
										{category.transactionCount}{" "}
										{category.transactionCount === 1 ? "lançamento" : "lançamentos"}
									</p>
								</div>
								<div className="flex items-center gap-3">
									<p className="text-sm font-semibold tabular-nums text-stone-950">
										{FinancePresentation.money(category.amountCents)}
									</p>
									<ChevronDown
										className={`size-4 text-stone-500 transition-transform ${isExpanded ? "rotate-180" : ""}`}
									/>
								</div>
							</button>
							{isExpanded && (
								<div id={transactionListId} className="divide-y divide-stone-100">
									{transactions
										.filter(
											(transaction) =>
												transaction.classification?.expenseCategory?.id === category.id,
										)
										.map((transaction) => (
											<OperatingExpenseRow
												key={transaction.id}
												categoryName={category.name}
												expanded={expandedTransactionIds.includes(transaction.id)}
												transaction={transaction}
												onToggle={() => {
													setExpandedTransactionIds((current) =>
														current.includes(transaction.id)
															? current.filter((transactionId) => transactionId !== transaction.id)
															: [...current, transaction.id],
													);
												}}
											/>
										))}
								</div>
							)}
						</section>
					);
				})}
			</section>
		</>
	);
}

function OperatingExpenseRow({
	categoryName,
	expanded,
	transaction,
	onToggle,
}: {
	categoryName: string;
	expanded: boolean;
	transaction: OperatingExpense;
	onToggle: () => void;
}) {
	return (
		<div>
			<button
				type="button"
				aria-expanded={expanded}
				onClick={onToggle}
				className="grid w-full gap-2 px-5 py-4 text-left transition hover:bg-stone-50 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
			>
				<div className="min-w-0">
					<p className="truncate text-sm font-semibold text-stone-900">
						{transaction.supplier?.name || transaction.receiverName || transaction.description}
					</p>
					<p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-stone-500">
						<span>{FinancePresentation.transactionDate(transaction.occurredOn)}</span>
						<span aria-hidden="true">·</span>
						<span className="inline-flex items-center gap-1 font-medium text-emerald-800">
							<CircleCheck className="size-3.5" />
							Classificada como {categoryName}
						</span>
						<span aria-hidden="true">·</span>
						<span>
							{transaction.fiscalDocuments.length > 0
								? `${transaction.fiscalDocuments.length} documento${transaction.fiscalDocuments.length === 1 ? "" : "s"}`
								: "sem documento fiscal ligado"}
						</span>
					</p>
				</div>
				<div className="flex items-center justify-between gap-3 sm:justify-end">
					<p className="text-sm font-semibold tabular-nums text-stone-950">
						{FinancePresentation.money(Math.abs(transaction.amountCents))}
					</p>
					<ChevronDown
						className={`size-4 text-stone-500 transition-transform ${expanded ? "rotate-180" : ""}`}
					/>
				</div>
			</button>
			{expanded && (
				<div className="border-t border-stone-100 bg-stone-50/60 px-5 py-4">
					<TransactionFiscalDocuments documents={transaction.fiscalDocuments} showEmpty />
				</div>
			)}
		</div>
	);
}
